import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Crosshair, Landmark, Search as SearchIcon, Target, Users } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, EventTypeChip, Loading, Select, StatCard, statusTone } from "../../components/ui";
import { useApi } from "../../api/client";
import { usePortalLink } from "../../utils/portalLink";
import { rowClick } from "../../utils/rowClick";
import { usePageView } from "../../utils/track";
import { MatchInfo } from "../../data/types";

/*
 * Every range the portal holds, across all events.
 *
 * The matches were reachable only from inside an event, which is the wrong way
 * round for the question people actually ask — "what was shot at 600 last
 * month?" rather than "what did Blue Mountains hold?". This lists them newest
 * first, in the same shape as the Events and Competitions lists, so the three
 * read alike and only their content differs.
 */
export default function Matches() {
  usePageView("matches");
  const link = usePortalLink();
  const navigate = useNavigate();
  const { data: matches, loading } = useApi<MatchInfo[]>("/matches", []);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [distanceFilter, setDistanceFilter] = useState("All Distances");

  const types = useMemo(
    () => Array.from(new Set(matches.map((m) => m.eventType).filter(Boolean))).sort() as string[],
    [matches]
  );
  const distances = useMemo(
    () => Array.from(new Set(matches.map((m) => m.distance).filter(Boolean))).sort(),
    [matches]
  );

  const q = search.trim().toLowerCase();
  const filtered = matches.filter(
    (m) =>
      (!q || [m.name, m.mid ?? "", m.distance, m.date].join(" ").toLowerCase().includes(q)) &&
      (statusFilter === "All Status" || m.status === statusFilter.toUpperCase()) &&
      (typeFilter === "All Types" || m.eventType === typeFilter) &&
      (distanceFilter === "All Distances" || m.distance === distanceFilter)
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-5 sm:px-5">
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Matches" }]} />

      <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink">Matches</h1>
      <p className="mt-0.5 text-[13px] text-muted">
        Every range shot across the portal — each one belongs to a competition, and each competition to an event.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Landmark size={20} />}
          value={new Set(matches.map((m) => m.competitionId)).size}
          label="Events"
          sub="Holding them"
          iconBg="bg-success-soft text-success"
        />
        <StatCard icon={<Crosshair size={20} />} value={matches.length} label="Matches" sub="On record" />
        <StatCard
          icon={<Target size={20} />}
          value={matches.reduce((a, m) => a + (m.shotsRecorded ?? 0), 0).toLocaleString()}
          label="Shots"
          sub="Recorded"
          iconBg="bg-violet-50 text-violet-600"
        />
        <StatCard
          icon={<Users size={20} />}
          value={matches.reduce((a, m) => a + (m.shooters ?? 0), 0).toLocaleString()}
          label="Entries"
          sub="Across every match"
          iconBg="bg-warning-soft text-warning"
        />
      </div>

      <div className="card mt-4 p-3">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
          <div className="min-w-0">
            <label className="label">Search</label>
            <div className="relative">
              <SearchIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input pl-9"
                placeholder="Match name, MID or distance..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="min-w-0">
            <label className="label">Status</label>
            <Select
              options={["All Status", "Live", "Completed", "Upcoming"]}
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-full lg:w-36"
            />
          </div>
          <div className="min-w-0">
            <label className="label">Distance</label>
            <Select
              options={["All Distances", ...distances]}
              value={distanceFilter}
              onChange={setDistanceFilter}
              className="w-full lg:w-36"
            />
          </div>
          <div className="min-w-0">
            <label className="label">Event Type</label>
            <Select options={["All Types", ...types]} value={typeFilter} onChange={setTypeFilter} className="w-full lg:w-36" />
          </div>
        </div>
      </div>

      <div className="card mt-4">
        {loading ? (
          <Loading />
        ) : !filtered.length ? (
          <EmptyState icon={Crosshair} title="No matches yet" message="Upload a shoot workbook and its ranges appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px]">
              <thead>
                <tr className="border-b border-line bg-slate-50">
                  <th className="th">Match</th>
                  <th className="th">Type</th>
                  <th className="th">Level</th>
                  <th className="th">Date</th>
                  <th className="th">Distance</th>
                  <th className="th text-center">Clubs</th>
                  <th className="th text-center">Shooters</th>
                  <th className="th text-center">Shots</th>
                  <th className="th">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((m) => (
                  <tr
                    key={m.id}
                    onClick={rowClick(() =>
                      navigate(link(`/competitions/${m.competitionId}/events/${m.eventId}/matches/${m.id}`))
                    )}
                    className="cursor-pointer transition-colors hover:bg-slate-50"
                  >
                    <td className="td">
                      <Link
                        to={link(`/competitions/${m.competitionId}/events/${m.eventId}/matches/${m.id}`)}
                        className="font-bold text-ink hover:text-primary"
                      >
                        {m.name}
                      </Link>
                      {m.mid && <div className="text-xs text-muted">MID {m.mid}</div>}
                    </td>
                    <td className="td"><EventTypeChip type={m.eventType} /></td>
                    <td className="td text-slate-600">{m.eventLevel || "—"}</td>
                    <td className="td whitespace-nowrap text-slate-600">{m.date}</td>
                    <td className="td text-slate-600">{m.distance}</td>
                    <td className="td text-center font-semibold">{m.clubs ?? 0}</td>
                    <td className="td text-center font-semibold">{m.shooters ?? 0}</td>
                    <td className="td text-center font-semibold">{(m.shotsRecorded ?? 0).toLocaleString()}</td>
                    <td className="td"><Badge tone={statusTone(m.status)}>{m.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
